// API Base URL - depuis les variables d'environnement
import API_BASE_URL from '../config/api';

/** Détail d'erreur FastAPI (string, tableau de validation, ou objet) → message lisible */
function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (typeof e === 'object' && e?.msg ? `${e.loc?.join?.('.') || ''}: ${e.msg}` : String(e)))
      .join('; ');
  }
  if (typeof detail === 'object') {
    try {
      return JSON.stringify(detail);
    } catch {
      return String(detail);
    }
  }
  return String(detail);
}

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Obtenir les headers d'authentification
  const authHeaders = getAuthHeaders();
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,  // Ajouter automatiquement les headers d'authentification
      ...(options.headers || {}),  // Permettre de surcharger les headers si nécessaire
    },
    ...options,
  };
  
  // Supprimer options.headers pour éviter la duplication
  delete defaultOptions.headers.undefined;

  try {
    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Session expirée/invalide : ne rediriger que si on avait un token (pas sur un simple
      // échec de connexion depuis la page de login, où l'on n'envoie encore aucun token).
      if (response.status === 401 && authHeaders.Authorization) {
        localStorage.removeItem('admin_access_token');
        window.location.href = '/admin-login';
        throw new Error('Votre session a expiré. Veuillez vous reconnecter.');
      }

      const msg = formatApiErrorDetail(errorData.detail) || `HTTP error! status: ${response.status}`;
      throw new Error(msg);
    }
    
    if (response.status === 204) {
      return null;
    }
    const text = await response.text();
    if (!text || !text.trim()) return null;
    return JSON.parse(text);
  } catch (error) {
    console.error('Admin API Error:', error);
    throw error;
  }
};

// Authentication helpers
const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// ==================== ADMIN AUTHENTICATION ====================

// Admin login
export const adminLogin = async (credentials) => {
  return apiCall('/admins/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });
};

// Get current admin info
export const getCurrentAdmin = async () => {
  return apiCall('/admins/me', {
    headers: getAuthHeaders(),
  });
};

// ==================== PROMOTIONS (VALIDATION) ====================

// ✨ NOUVEAU: Récupérer les promotions en attente de validation
export const getPromotionsPending = async () => {
  return apiCall('/admins/promotions?status=pending', {
    headers: getAuthHeaders(),
  });
};

// ✨ NOUVEAU: Valider une promotion
export const validatePromotion = async (promotionId) => {
  return apiCall(`/admins/promotions/${promotionId}/validate`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });
};

// ✨ NOUVEAU: Rejeter une promotion
export const rejectPromotion = async (promotionId, reason) => {
  return apiCall(`/admins/promotions/${promotionId}/reject`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason }),
  });
};

// ==================== ANNOUNCEMENTS ====================

export const getAdminAnnouncements = async () => {
  return apiCall('/admins/announcements', {
    headers: getAuthHeaders(),
  });
};

export const createAdminAnnouncement = async (payload) => {
  return apiCall('/admins/announcements', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
};

export const updateAdminAnnouncement = async (announcementId, payload) => {
  return apiCall(`/admins/announcements/${announcementId}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
};

export const deleteAdminAnnouncement = async (announcementId) => {
  return apiCall(`/admins/announcements/${announcementId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

// ✨ NOUVEAU: Récupérer toutes les promotions avec leurs statistiques
export const getAllPromotions = async (status = null, skip = 0, limit = 100) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  params.append('skip', skip.toString());
  params.append('limit', limit.toString());
  
  return apiCall(`/admins/promotions/all?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
};

// ✨ NOUVEAU: Récupérer les statistiques globales des promotions
export const getPromotionsStats = async () => {
  return apiCall('/admins/promotions/stats', {
    headers: getAuthHeaders(),
  });
};

// ==================== PROMOTION PRICING CONFIG ====================

export const getAdminPromotionPricingConfigs = async () => {
  return apiCall('/admins/promotion-pricing', {
    headers: getAuthHeaders(),
  });
};

export const createAdminPromotionPricingConfig = async (payload) => {
  return apiCall('/admins/promotion-pricing', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
};

export const updateAdminPromotionPricingConfig = async (displayFormat, payload) => {
  return apiCall(`/admins/promotion-pricing/${displayFormat}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
};

// ==================== ADMIN DASHBOARD ====================

// Get admin dashboard statistics
export const getAdminStats = async () => {
  return apiCall('/admins/stats/dashboard', {
    headers: getAuthHeaders(),
  });
};

/** Séries journalières (commandes, CA), indices et répartition des statuts */
export const getAdminDailyReport = async (days = 14) => {
  return apiCall(`/admins/stats/dashboard/daily?days=${days}`, {
    headers: getAuthHeaders(),
  });
};

// Get recent activities
export const getRecentActivities = async (limit = 10) => {
  return apiCall(`/admins/activities/recent?limit=${limit}`, {
    headers: getAuthHeaders(),
  });
};

// Get top restaurants by performance
export const getTopRestaurants = async (limit = 5) => {
  return apiCall(`/admins/restaurants/top?limit=${limit}`, {
    headers: getAuthHeaders(),
  });
};

// Get top users by spending/orders
export const getTopUsers = async (limit = 5) => {
  return apiCall(`/admins/users/top?limit=${limit}`, {
    headers: getAuthHeaders(),
  });
};

// ==================== USER MANAGEMENT ====================

// Get all users with pagination and filters
export const getAllUsers = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, value);
    }
  });

  const url = `/admins/users${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return apiCall(url, {
    headers: getAuthHeaders(),
  });
};

// Suspend a user
export const suspendUser = async (userId) => {
  return apiCall(`/admins/users/${userId}/suspend`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });
};

// Activate a user
export const activateUser = async (userId) => {
  return apiCall(`/admins/users/${userId}/activate`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });
};

// Delete a user
export const deleteUser = async (userId) => {
  return apiCall(`/admins/users/${userId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

// Create admin (API: email + password uniquement)
export const createAdmin = async ({ email, password }) => {
  return apiCall('/admins/', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email: String(email).trim(), password }),
  });
};

// Get all admins
export const getAllAdmins = async () => {
  return apiCall('/admins/', {
    headers: getAuthHeaders(),
  });
};

// Update admin (email requis ; mot de passe optionnel pour le changer)
export const updateAdmin = async (email, adminData) => {
  const payload = { email: String(adminData.email).trim() };
  if (adminData.password && String(adminData.password).length > 0) {
    payload.password = adminData.password;
  }
  return apiCall(`/admins/${encodeURIComponent(email)}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
};

// Delete admin
export const deleteAdmin = async (email) => {
  return apiCall(`/admins/${encodeURIComponent(email)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

// ==================== UTILITY FUNCTIONS ====================

// Format date for admin interface
export const formatAdminDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Format currency (arrondi à 2 décimales)
export const formatCurrency = (amount) => {
  const roundedAmount = Math.round((amount || 0) * 100) / 100; // Arrondir à 2 décimales
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(roundedAmount);
};

// Get status badge color
export const getStatusColor = (status) => {
  const colors = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-red-100 text-red-800',
    pending: 'bg-yellow-100 text-yellow-800',
    suspended: 'bg-gray-100 text-gray-800',
    resolved: 'bg-blue-100 text-blue-800',
    open: 'bg-orange-100 text-orange-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

// Error handler for admin API
export const handleAdminApiError = (error) => {
  console.error('Admin API Error:', error);
  return {
    error: true,
    message: error.message || 'Une erreur est survenue dans l\'administration',
  };
};

// ==================== DISH MANAGEMENT ====================

// Get all dishes
export const getAllDishes = async () => {
  return apiCall('/admins/dishes', {
    headers: getAuthHeaders(),
  });
};

// Delete dish
export const deleteDish = async (dishId) => {
  return apiCall(`/admins/dishes/${dishId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

// Suspend dish
export const suspendDish = async (dishId) => {
  return apiCall(`/admins/dishes/${dishId}/suspend`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });
};

// Delete restaurant
export const deleteRestaurant = async (restaurantId) => {
  return apiCall(`/admins/restaurants/${restaurantId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

// Delete order
export const deleteOrder = async (orderId) => {
  return apiCall(`/admins/orders/${orderId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

// ==================== STATISTICS ====================

// Dashboard statistics
export const getDashboardStats = async () => {
  return apiCall('/admins/stats/dashboard', {
    headers: getAuthHeaders(),
  });
};

export const getUserStats = async () => {
  return apiCall('/admins/stats/users', {
    headers: getAuthHeaders(),
  });
};

export const getRestaurantStats = async () => {
  return apiCall('/admins/stats/restaurants', {
    headers: getAuthHeaders(),
  });
};

export const getOrderStats = async () => {
  return apiCall('/admins/stats/orders', {
    headers: getAuthHeaders(),
  });
};

export const getRevenueStats = async () => {
  return apiCall('/admins/stats/revenue', {
    headers: getAuthHeaders(),
  });
};

// ==================== PAYMENT MANAGEMENT ====================

// Get all payments
export const getAllPayments = async () => {
  return apiCall('/admins/payments', {
    headers: getAuthHeaders(),
  });
};

export const getPaymentDetails = async (paymentId) => {
  return apiCall(`/admins/payments/${paymentId}`, {
    headers: getAuthHeaders(),
  });
};

export const getPaymentStats = async () => {
  return apiCall('/admins/stats/payments', {
    headers: getAuthHeaders(),
  });
};

export const getPlatformCommissions = async () => {
  return apiCall('/admins/platform/commissions', {
    headers: getAuthHeaders(),
  });
};

export const platformWithdraw = async (amount) => {
  return apiCall('/admins/platform/withdraw', {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ amount }),
  });
};

// ==================== MARKETING ====================

export const getMarketingStats = async () => {
  return apiCall('/admins/stats/marketing', {
    headers: getAuthHeaders(),
  });
};

export const createCampaign = async (campaignData) => {
  return apiCall('/admins/campaigns', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(campaignData),
  });
};

export const updateCampaign = async (campaignId, campaignData) => {
  return apiCall(`/admins/campaigns/${campaignId}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(campaignData),
  });
};

export const deleteCampaign = async (campaignId) => {
  return apiCall(`/admins/campaigns/${campaignId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

// ==================== SYSTEM SETTINGS ====================

export const getSystemSettings = async () => {
  return apiCall('/admins/settings', {
    headers: getAuthHeaders(),
  });
};

export const updateSystemSettings = async (settings) => {
  return apiCall('/admins/settings', {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(settings),
  });
};

// ==================== RESTAURANT MANAGEMENT ====================

// Get all restaurants with pagination and filters
export const getAllRestaurants = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, value);
    }
  });

  const url = `/admins/restaurants${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return apiCall(url, {
    headers: getAuthHeaders(),
  });
};

// Suspend a restaurant (optionnel : message affiché aux visiteurs sur lien direct)
export const suspendRestaurant = async (restaurantId, options = {}) => {
  const payload = {};
  if (options.suspension_note != null && String(options.suspension_note).trim() !== '') {
    payload.suspension_note = String(options.suspension_note).trim();
  }
  return apiCall(`/admins/restaurants/${restaurantId}/suspend`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

// Activate a restaurant
export const activateRestaurant = async (restaurantId) => {
  return apiCall(`/admins/restaurants/${restaurantId}/activate`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });
};

// Validate a restaurant
export const validateRestaurant = async (restaurantId) => {
  return apiCall(`/admins/restaurants/${restaurantId}/validate`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });
};

// ==================== CERTIFICATION RESTAURANT (documents) ====================

// Liste les documents justificatifs envoyés par un restaurant
export const getRestaurantDocuments = async (restaurantId) => {
  return apiCall(`/admins/restaurants/${restaurantId}/documents`, {
    headers: getAuthHeaders(),
  });
};

// Approuve ou rejette un document ({ status: 'approved' | 'rejected', rejection_reason? })
export const reviewRestaurantDocument = async (restaurantId, documentId, review) => {
  return apiCall(`/admins/restaurants/${restaurantId}/documents/${documentId}/review`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(review),
  });
};

// Accorde le badge de certification (nécessite au moins un document approuvé)
export const certifyRestaurant = async (restaurantId) => {
  return apiCall(`/admins/restaurants/${restaurantId}/certify`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });
};

// Retire le badge de certification
export const decertifyRestaurant = async (restaurantId) => {
  return apiCall(`/admins/restaurants/${restaurantId}/decertify`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });
};

// ==================== GROCERY SHOP MANAGEMENT ====================

// Get all grocery shops (plateforme)
export const getAllGroceryShops = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, value);
    }
  });
  const url = `/admins/grocery-shops/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return apiCall(url, {
    headers: getAuthHeaders(),
  });
};

// Activer / suspendre un grocery shop
export const setGroceryShopActive = async (groceryShopId, isActive) => {
  return apiCall(`/admins/grocery-shops/${groceryShopId}`, {
    method: 'PUT',
    body: JSON.stringify({ is_active: isActive }),
  });
};

// Supprimer un grocery shop
export const deleteGroceryShop = async (groceryShopId) => {
  return apiCall(`/admins/grocery-shops/${groceryShopId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

// ==================== ORDER MANAGEMENT ====================

// Get all orders with pagination and filters
export const getAllOrders = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, value);
    }
  });

  const url = `/admins/orders${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return apiCall(url, {
    headers: getAuthHeaders(),
  });
};

// Update order status
export const updateOrderStatus = async (orderId, status) => {
  return apiCall(`/admins/orders/${orderId}/status`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status }),
  });
};

// Cancel order
export const cancelOrder = async (orderId) => {
  return apiCall(`/admins/orders/${orderId}/cancel`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });
};

// ==================== COMPLAINT MANAGEMENT ====================

// Get all complaints with pagination and filters
export const getAllComplaints = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, value);
    }
  });

  const url = `/admins/complaints${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return apiCall(url, {
    headers: getAuthHeaders(),
  });
};

// Resolve complaint
export const resolveComplaint = async (complaintId) => {
  return apiCall(`/admins/complaints/${complaintId}/resolve`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });
};

// Reject complaint
export const rejectComplaint = async (complaintId) => {
  return apiCall(`/admins/complaints/${complaintId}/reject`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });
};

// ==================== RÉFÉRENCE GÉO & CUISINE (inscription restaurateur) ====================

export const getAdminReferenceCountries = async (includeInactive = true) => {
  const q = includeInactive ? '?include_inactive=true' : '?include_inactive=false';
  return apiCall(`/admins/reference/countries${q}`, { headers: getAuthHeaders() });
};

// ==================== NOTIFICATIONS ADMIN ====================

export const getAdminNotifications = async () => {
  return apiCall('/admins/notifications', { headers: getAuthHeaders() });
};

export const getAdminNotificationsUnreadCount = async () => {
  return apiCall('/admins/notifications/unread-count', { headers: getAuthHeaders() });
};

export const markAdminNotificationsSeen = async () => {
  return apiCall('/admins/notifications/mark-seen', { method: 'POST', headers: getAuthHeaders() });
};

export const createAdminReferenceCountry = async (data) => {
  return apiCall('/admins/reference/countries', {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
};

export const updateAdminReferenceCountry = async (id, data) => {
  return apiCall(`/admins/reference/countries/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
};

export const deleteAdminReferenceCountry = async (id) => {
  return apiCall(`/admins/reference/countries/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

export const getAdminReferenceCities = async (countryId = null, includeInactive = true) => {
  const params = new URLSearchParams();
  if (countryId != null) params.set('country_id', String(countryId));
  params.set('include_inactive', includeInactive ? 'true' : 'false');
  return apiCall(`/admins/reference/cities?${params}`, { headers: getAuthHeaders() });
};

export const createAdminReferenceCity = async (data) => {
  return apiCall('/admins/reference/cities', {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
};

export const updateAdminReferenceCity = async (id, data) => {
  return apiCall(`/admins/reference/cities/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
};

export const deleteAdminReferenceCity = async (id) => {
  return apiCall(`/admins/reference/cities/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

export const getAdminReferenceCuisineTypes = async (includeInactive = true) => {
  const q = includeInactive ? '?include_inactive=true' : '?include_inactive=false';
  return apiCall(`/admins/reference/cuisine-types${q}`, { headers: getAuthHeaders() });
};

export const createAdminReferenceCuisineType = async (data) => {
  return apiCall('/admins/reference/cuisine-types', {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
};

export const updateAdminReferenceCuisineType = async (id, data) => {
  return apiCall(`/admins/reference/cuisine-types/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
};

export const deleteAdminReferenceCuisineType = async (id) => {
  return apiCall(`/admins/reference/cuisine-types/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

// ==================== CATÉGORIES DE PLATS (home page) ====================

export const getAdminDishCategories = async () => {
  return apiCall('/categories/', { headers: getAuthHeaders() });
};

export const createAdminDishCategory = async (data) => {
  return apiCall('/categories/', {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
};

export const updateAdminDishCategory = async (id, data) => {
  return apiCall(`/categories/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
};

export const deleteAdminDishCategory = async (id) => {
  return apiCall(`/categories/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

// ==================== DEFAULT EXPORT ====================

export default {
  // Admin authentication
  adminLogin,
  getCurrentAdmin,
  createAdmin,
  getAllAdmins,
  updateAdmin,
  deleteAdmin,
  
  // User management
  getAllUsers,
  deleteUser,
  suspendUser,
  activateUser,
  
  // Restaurant management
  getAllRestaurants,
  deleteRestaurant,
  suspendRestaurant,
  activateRestaurant,
  validateRestaurant,
  getRestaurantDocuments,
  reviewRestaurantDocument,
  certifyRestaurant,
  decertifyRestaurant,

  // Dish management
  getAllDishes,
  deleteDish,
  suspendDish,
  
  // Order management
  getAllOrders,
  deleteOrder,
  updateOrderStatus,
  cancelOrder,
  
  // Complaint management
  getAllComplaints,
  resolveComplaint,
  rejectComplaint,
  
  // Promotions (validation)
  getPromotionsPending,
  validatePromotion,
  rejectPromotion,
  getAdminAnnouncements,
  createAdminAnnouncement,
  updateAdminAnnouncement,
  deleteAdminAnnouncement,
  getAllPromotions,
  getPromotionsStats,
  getAdminPromotionPricingConfigs,
  createAdminPromotionPricingConfig,
  updateAdminPromotionPricingConfig,
  
  // Statistics
  getDashboardStats,
  getUserStats,
  getRestaurantStats,
  getOrderStats,
  getRevenueStats,
  getAdminStats,
  getAdminDailyReport,
  getRecentActivities,
  getTopRestaurants,
  getTopUsers,
  
  // Payment management
  getAllPayments,
  getPaymentDetails,
  getPaymentStats,
  
  // Marketing
  getMarketingStats,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  
  // System settings
  getSystemSettings,
  updateSystemSettings,

  // Notifications admin
  getAdminNotifications,
  getAdminNotificationsUnreadCount,
  markAdminNotificationsSeen,

  // Référence géo & cuisine
  getAdminReferenceCountries,
  createAdminReferenceCountry,
  updateAdminReferenceCountry,
  deleteAdminReferenceCountry,
  getAdminReferenceCities,
  createAdminReferenceCity,
  updateAdminReferenceCity,
  deleteAdminReferenceCity,
  getAdminReferenceCuisineTypes,
  createAdminReferenceCuisineType,
  updateAdminReferenceCuisineType,
  deleteAdminReferenceCuisineType,

  // Catégories de plats
  getAdminDishCategories,
  createAdminDishCategory,
  updateAdminDishCategory,
  deleteAdminDishCategory,

  // Utility functions
  formatAdminDate,
  formatCurrency,
  getStatusColor,
  handleAdminApiError,
};
